import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { MdEmail, MdCheckCircle } from 'react-icons/md';
import BrandLogo from '../../components/common/BrandLogo';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import { forgotPassword } from '../../services/authService';
import useForceDarkTheme from '../../hooks/useForceDarkTheme';

function ForgotPassword() {
  useForceDarkTheme();
  const [sent, setSent]       = useState(false);
  const [loading, setLoading] = useState(false);
  const { register, handleSubmit, formState: { errors, isValid } } = useForm({ mode: 'onChange' });

  const onSubmit = async ({ email }) => {
    if (loading) return;
    setLoading(true);
    try {
      await forgotPassword(email.trim().toLowerCase());
      setSent(true);
    } catch {
      setSent(true); // always show same success UI — do not reveal whether email exists
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4">
      <div className="flex items-center gap-2 mb-8">
        <BrandLogo size={28} />
        <span className="font-bold text-secondary text-xl">TrainAxis</span>
      </div>

      <div className="bg-[#1e1d1b] rounded-2xl border border-[rgba(237,232,224,0.08)] shadow-2xl p-6 max-w-sm w-full">
        {!sent ? (
          <>
            <h2 className="font-bold text-secondary text-xl mb-1">Forgot Password</h2>
            <p className="text-[#8a847c] text-sm mb-5">Enter your email and we'll send you a reset link.</p>
            <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
              <Input
                icon={MdEmail}
                type="email"
                placeholder="Email address"
                autoComplete="email"
                error={errors.email?.message}
                {...register('email', {
                  required: 'Email is required.',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address.' },
                })}
              />
              <Button type="submit" fullWidth loading={loading} disabled={!isValid || loading}>
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </form>
            <p className="text-center text-sm text-[#8a847c] mt-4">
              <Link to="/login" className="text-primary font-semibold hover:underline">Back to Login</Link>
            </p>
          </>
        ) : (
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-green-400/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <MdCheckCircle className="text-green-500 text-4xl" />
            </div>
            <h2 className="font-bold text-secondary text-xl mb-2">Check your inbox</h2>
            <p className="text-[#8a847c] text-sm mb-3">If this email exists, reset instructions have been sent.</p>
            <p className="text-[#8a847c] text-xs mb-1">Check your inbox and spam folder.</p>
            <p className="text-[#8a847c] text-xs mb-6">Still stuck? Contact your club admin to reset your password manually.</p>
            <Link to="/login"><Button fullWidth>Back to Login</Button></Link>
          </div>
        )}
      </div>
    </div>
  );
}

export default ForgotPassword;
